"use client";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@workspace/ui/components/sheet";
import { cn } from "@workspace/ui/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { InfoIcon, Loader2, MessageCircleIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import selcaIcon from "../../selca-icon-min.png";
import type { MappedRequest } from "../ImagesPage";
import { LiquidGlass } from "../ui/LiquidGlass";
import { DetailPanelChat } from "./DetailPanelChat";
import { DetailPanelInfo } from "./DetailPanelInfo";

type Tab = "chat" | "info";

type ImagesDetailPanelProps = {
  request: MappedRequest | null;
  onClose: () => void;
  open: boolean;
  isMobile: boolean;
  widthPercent?: number;
};

const PANEL_EASE = [0.22, 1, 0.36, 1] as const;

export function ImagesDetailPanel({
  request,
  onClose,
  open,
  isMobile,
  widthPercent = 35,
}: ImagesDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>("chat");

  useEffect(() => {
    if (!isMobile) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [onClose, isMobile]);

  if (!request) return null;

  const panelContent = (
    <>
      <PanelHeader
        activeTab={activeTab}
        onClose={onClose}
        onTabChange={setActiveTab}
        request={request}
        showClose={!isMobile}
      />

      <div className="flex-1 min-h-0 overflow-hidden">
        <AnimatePresence mode="wait">
          {activeTab === "chat" ? (
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              className="h-full"
              exit={{ opacity: 0, x: -10 }}
              initial={{ opacity: 0, x: 10 }}
              key="chat"
              transition={{ duration: 0.2 }}
            >
              <DetailPanelChat request={request} />
            </motion.div>
          ) : (
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              className="h-full"
              exit={{ opacity: 0, x: 10 }}
              initial={{ opacity: 0, x: -10 }}
              key="info"
              transition={{ duration: 0.2 }}
            >
              <DetailPanelInfo request={request} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Sheet onOpenChange={(isOpen) => !isOpen && onClose()} open={open}>
        <SheetContent className="h-[85vh] p-0 flex flex-col" side="bottom">
          <SheetTitle className="sr-only">Request Details</SheetTitle>
          <SheetDescription className="sr-only">
            View and interact with your image request
          </SheetDescription>
          {panelContent}
        </SheetContent>
      </Sheet>
    );
  }

  if (!open) return null;

  return (
    <motion.div
      animate={{ opacity: 1, x: 0 }}
      className="flex-shrink-0"
      exit={{ opacity: 0, x: 20 }}
      initial={{ opacity: 0, x: 20 }}
      style={{ width: `${widthPercent}%` }}
      transition={{ duration: 0.3, ease: PANEL_EASE }}
    >
      <LiquidGlass
        blurAmount={20}
        className="flex h-full flex-col border border-zinc-200/50 dark:border-white/10"
        cornerRadius={20}
        opacity={15}
        padding="0"
        saturation={130}
      >
        {panelContent}
      </LiquidGlass>
    </motion.div>
  );
}

function PanelHeader({
  request,
  onClose,
  activeTab,
  onTabChange,
  showClose = true,
}: {
  request: MappedRequest;
  onClose: () => void;
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  showClose?: boolean;
}) {
  const isProcessing = request.stage === "processing";

  return (
    <div className="shrink-0 border-b border-zinc-200/50 dark:border-white/10">
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-200 to-orange-300 shadow-sm">
            <img
              alt="Selca"
              className="size-7 object-contain"
              src={selcaIcon}
            />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-sm text-foreground">
              {request.promptText
                ? request.promptText.slice(0, 30) +
                  (request.promptText.length > 30 ? "..." : "")
                : "Untitled Request"}
            </span>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {isProcessing ? (
                <>
                  <Loader2 className="size-3 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <span className="capitalize">{request.stage}</span>
              )}
            </div>
          </div>
        </div>

        {showClose && (
          <button
            className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-zinc-200/50 dark:hover:bg-white/10 hover:text-foreground"
            onClick={onClose}
            type="button"
          >
            <XIcon className="size-5" />
          </button>
        )}
      </div>

      <div className="flex gap-1 px-4 pb-2">
        <TabButton
          icon={<MessageCircleIcon className="size-4" />}
          isActive={activeTab === "chat"}
          label="Chat"
          onClick={() => onTabChange("chat")}
        />
        <TabButton
          icon={<InfoIcon className="size-4" />}
          isActive={activeTab === "info"}
          label="Info"
          onClick={() => onTabChange("info")}
        />
      </div>
    </div>
  );
}

function TabButton({
  icon,
  label,
  isActive,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={cn(
        "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200",
        isActive
          ? "bg-zinc-200/60 dark:bg-white/15 text-foreground"
          : "text-muted-foreground hover:bg-zinc-200/40 dark:hover:bg-white/10"
      )}
      onClick={onClick}
      type="button"
    >
      {icon}
      {label}
    </button>
  );
}
